import React, { useCallback } from 'react'
import { Chip, SearchField } from '@adc/ui-core'
import { search } from '@adc/blueprint'
import type {
  BuyingCriteriaLocation,
  LocationSearchResult,
} from '@adc/blueprint'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

export interface LocationSectionProps {
  /** Current selected locations */
  locations: BuyingCriteriaLocation[]
  /** Called when locations change (add/remove) */
  onChange: (locations: BuyingCriteriaLocation[]) => void
  /** Maximum number of locations allowed. Default: 5 */
  maxLocations?: number
  /** Placeholder text for the search input */
  placeholder?: string
  /** Automation ID prefix */
  elmId?: string
  /** Focus the search input when the section mounts */
  autoFocus?: boolean
}

/**
 * LocationSection — Async location search with multi-location chips.
 *
 * Uses `useLocationSearch` from `@adc/blueprint/search` for the
 * search API call, debouncing, and result formatting.
 *
 * @example
 * <LocationSection
 *   locations={criteria.locations}
 *   onChange={(locs) => actions.updateLocations(locs)}
 * />
 */
const LocationSection: React.FC<LocationSectionProps> = ({
  locations,
  onChange,
  maxLocations = 5,
  placeholder = 'Enter State, County, City, or ZIP and press Enter',
  elmId = 'buying-criteria-location',
  autoFocus = false,
}) => {
  const { data, actions } = search.locationSearch.useLocationSearch({
    includeAddresses: false,
  })

  const handleSearchChange = useCallback(
    (_e: unknown, { value }: { value: string }) => {
      actions.search(value)
    },
    [actions]
  )

  const handleResultSelect = useCallback(
    (_e: unknown, { result }: { result: LocationSearchResult }) => {
      const location = actions.selectResult(result)
      if (location && !locations.some(l => l.value === location.value)) {
        if (locations.length < maxLocations) {
          onChange([...locations, location])
        }
      }
    },
    [actions, locations, onChange, maxLocations]
  )

  const handleRemoveLocation = useCallback(
    (locationValue: string) => {
      onChange(locations.filter(l => l.value !== locationValue))
    },
    [locations, onChange]
  )

  return (
    <div className={cx('location-section')}>
      <SearchField
        elmId={`${elmId}-search`}
        autoFocus={autoFocus}
        placeholder={placeholder}
        results={data.searchResults}
        onSearchChange={handleSearchChange}
        onResultSelect={handleResultSelect}
        compact={false}
        fluid
        loading={data.isSearching}
        disabled={locations.length >= maxLocations}
      />

      {locations.length >= maxLocations && (
        <p className={cx('max-locations-hint')}>
          Maximum of {maxLocations} locations reached.
        </p>
      )}

      {locations.length > 0 && (
        <div className={cx('location-chips')}>
          {locations.map(location => (
            <Chip
              key={location.value}
              elmId={`${elmId}-chip-${location.value}`}
              type='input'
              content={location.label}
              selectionStyle='light'
              onClick={() => handleRemoveLocation(location.value)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default LocationSection
